import { useEffect, useState } from 'react'
import type { JSX } from 'react'

import {
  Button,
  Dialog,
  Flex,
  IconPicker,
  Input,
  Label,
  Text,
  VisuallyHidden,
} from '@buffer-mono/popcorn'
import type { IconPickerValue } from '@buffer-mono/popcorn'

import { useUpdateSavedViewMutation } from './hooks'
import type { SavedView } from './hooks'
import { savedViewIdentity } from './savedViewIdentity'
import type { DialogOpenProps } from './types'
import { validateViewName } from './validateViewName'

import styles from './SaveViewDialog.module.css'

type RenameViewDialogProps = DialogOpenProps & { view: SavedView }

/** Dialog for renaming an existing saved view. */
export function RenameViewDialog({
  view,
  open,
  onOpenChange,
}: RenameViewDialogProps): JSX.Element {
  const [updateSavedView, { loading }] = useUpdateSavedViewMutation()

  const [name, setName] = useState(view.name)
  const [identity, setIdentity] = useState<IconPickerValue>(() =>
    savedViewIdentity(view),
  )
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  // Reset local state whenever the dialog transitions to closed so a
  // subsequent open starts fresh from the current view's name and identity.
  useEffect(() => {
    if (!open) {
      setName(view.name)
      setIdentity(savedViewIdentity(view))
      setErrorMessage(null)
    }
  }, [open, view])

  const submit = async (): Promise<void> => {
    const error = validateViewName(name, { submitting: true })
    setErrorMessage(error)
    if (error) return

    const trimmed = name.trim()
    const stored = savedViewIdentity(view)
    const identityChanged =
      identity.icon !== stored.icon || identity.color !== stored.color
    if (trimmed === view.name && !identityChanged) {
      onOpenChange(false)
      return
    }

    // Only send the identity when the user actually changed it — a name-only
    // rename must not backfill defaults onto legacy views that store nulls.
    const outcome = await updateSavedView({
      id: view.id,
      name: trimmed,
      ...(identityChanged
        ? { icon: identity.icon, color: identity.color }
        : {}),
      optimistic: view,
    })

    if (outcome.success) {
      onOpenChange(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Content size="medium">
        <Dialog.Header>
          <Dialog.Title>Rename view</Dialog.Title>
        </Dialog.Header>
        <VisuallyHidden>
          <Dialog.Description>
            Change the name of this saved view.
          </Dialog.Description>
        </VisuallyHidden>
        <Dialog.Body>
          <form
            onSubmit={(event): void => {
              event.preventDefault()
              void submit()
            }}
          >
            <Label htmlFor="renameViewName" className={styles.label}>
              Name
            </Label>
            <Flex gap="xs" align="center">
              <IconPicker value={identity} onValueChange={setIdentity} modal>
                <IconPicker.Trigger />
                <IconPicker.Content />
              </IconPicker>
              <Input
                id="renameViewName"
                className={styles.nameInput}
                size="large"
                value={name}
                aria-required
                aria-invalid={!!errorMessage}
                aria-describedby={
                  errorMessage ? 'renameViewNameError' : undefined
                }
                onChange={(event): void => {
                  const next = event.target.value
                  setName(next)
                  setErrorMessage(validateViewName(next))
                }}
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
              />
            </Flex>
            {errorMessage && (
              <Text
                id="renameViewNameError"
                color="critical"
                role="alert"
                className={styles.errorLabel}
              >
                {errorMessage}
              </Text>
            )}

            <Flex
              gap="xs"
              justify="start"
              direction="row-reverse"
              className={styles.formButtons}
            >
              <Button
                variant="primary"
                size="large"
                type="submit"
                loading={loading}
              >
                Save
              </Button>
              <Button
                variant="tertiary"
                size="large"
                type="button"
                onClick={(): void => onOpenChange(false)}
              >
                Cancel
              </Button>
            </Flex>
          </form>
        </Dialog.Body>
      </Dialog.Content>
    </Dialog>
  )
}
